import React, { useContext, useEffect, useState } from "react";
import { Container, Row, Col, Button } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { IP_PATHS } from "../../../../Routes/paths";
import { useGlobalStudy } from "../../../../context/StudyContext";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import { useIpInventoryService } from "../api/useIpInventoryService";
import { formatDate } from "../../../../common/service/CommonService";
import SectionCard from "Components/Common/SectionCard";
import FullPageLoader from "Components/Common/FullPageLoader";
import FilePreviewModal from "Components/Common/FilePreviewModal";
import addVolunteerStyle from "../../../Volunteer/MasterVolunteer/css/volunteer.module.scss";
import { getIPCategoryLabel } from "../helpers/ipInventory.constant";
import DetailField from "../../../../Components/Common/DetailField";
import { BreadcrumbContext } from "Layouts/Header";
import TruncatedText from "Components/Common/TruncatedText";
import { useCheckListMasterService } from "modules/Library/Checklist/api/useChecklist";
import { CheckListQuestion } from "modules/Library/Checklist/helpers/checklist.types";
import { CoaDetailRow } from "../helpers/ipInventory.types";
import CoaDetailsTable from "../components/CoaDetailsTable";
type IpViewTab = "ip_details" | "acceptance_details";

const VIEW_TABS: { key: IpViewTab; label: string; icon: string }[] = [
  { key: "ip_details", label: "IP Inventory Details", icon: "ri-medicine-bottle-line" },
  { key: "acceptance_details", label: "IP Acceptance Sheet", icon: "ri-checkbox-circle-line" },
];

const ViewIpInventory: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<IpViewTab>("ip_details");
  const breadcrumbContext = useContext(BreadcrumbContext);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [coaList, setCoaList] = useState<CoaDetailRow[]>([]);

  const handlePreview = (file: any) => {
   
   const API_BASE_URL =  sessionStorage.getItem("baseUrl") || process.env.REACT_APP_BASE_URL
   const baseUrl = API_BASE_URL?.replace("/api", "") || "";

    const path = file?.filepath || file?.path;
    if (!path) return;

    const finalUrl = path.startsWith("http")
      ? path
      : `${baseUrl}${path.startsWith("/") ? path : "/" + path}`;

    setPreviewFile(null);
    setPreviewFileUrl(finalUrl);
    setPreviewOpen(true);
  };

  const { id } = useParams();

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Investigational Product", link: IP_PATHS.LIST },
      { text: "View IP Inventory" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, []);

  const { selectedStudy: globalStudy } = useGlobalStudy();
  const selectedreceiptnumber = sessionStorage.getItem("receiptnumber");

  const { SelectIdQuery } = useIpInventoryService({
    ipid: id,
    studyid: globalStudy?.studyid,
    receiptnumber: selectedreceiptnumber,
  });
  const selectQuery = SelectIdQuery;

  const data = selectQuery.data?.data?.[0];

  useEffect(() => {
    if (data?.coadetails && Array.isArray(data.coadetails) && data.coadetails.length > 0) {
      const mappedCoaList: CoaDetailRow[] = data.coadetails.map((detail: any) => ({
        coaid: detail.coaid ?? null,
        particulars: detail.checklistquestion,
        coaInfo: detail.coaanswer || "yes",
        labelInfo: detail.labelanswer || "yes",
      }));
      setCoaList(mappedCoaList);
    }
  }, [data?.coadetails]);

  if (selectQuery.isLoading || selectQuery.isFetching) {
    return (
      <Container fluid className={`px-0 ${formStyles.pageContainer}`}>
        <FullPageLoader />
      </Container>
    );
  }

  /* ── Not Found ── */
  if (!data) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ri-error-warning-line display-3 text-warning mb-3 d-block" />
            <h4 className="fw-600">Record Not Found</h4>
            <p className="text-muted">
              The IP Inventory record you are looking for does not exist or has been removed.
            </p>
          </div>
        </Container>
      </div>
    );
  }

  const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
    "T": { bg: "bg-primary-subtle", text: "text-primary", border: "border-primary" },
    "R": { bg: "bg-info-subtle", text: "text-info", border: "border-info" },
    "Test Product": { bg: "bg-primary-subtle", text: "text-primary", border: "border-primary" },
    "Reference Product": { bg: "bg-info-subtle", text: "text-info", border: "border-info" },
    "Placebo": { bg: "bg-warning-subtle", text: "text-warning", border: "border-warning" },
    "Ancillary / Supply": { bg: "bg-secondary-subtle", text: "text-secondary", border: "border-secondary" },
  };

  const catStyle =
    categoryColors[data.ipcategory?.toUpperCase()?.[0]] ||
    categoryColors[data.ipcategory] ||
    categoryColors["Test Product"];

  const categoryBadge = (
    <span
      className={`badge ${catStyle.bg} ${catStyle.text} px-3 py-2 rounded-pill fs-13 border ${catStyle.border}`}
    >
      {getIPCategoryLabel(data.ipcategory) || "—"}
    </span>
  );

  /* ── Render ── */
  return (
    <div className="page-content">
      <Container fluid>
        {/* ── Header ── */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="fw-700 mb-1">IP Inventory Details</h3>
            <p className="text-muted mb-0">
              Study: {data.studycode}&nbsp;|&nbsp;
              IP Category: {data.ipcategory}&nbsp;|&nbsp;
              Product: <TruncatedText text={data.productname} maxWidth="200px" />&nbsp;|&nbsp;
              {data.batchorlot === "lot" ? "Lot" : "Batch"}: <TruncatedText text={data.batchlotno} maxWidth="150px" />
            </p>
          </div>
        </div>

        {/* ── Tab Switcher ── */}
        <div className="mb-4">
          <div className="p-1 bg-light rounded-pill d-inline-flex border">
            {VIEW_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`btn btn-sm px-3 rounded-pill fw-semibold border-0 d-flex align-items-center gap-1 ${
                  activeTab === tab.key ? "text-primary shadow-sm" : "text-muted"
                }`}
                style={{
                  fontSize: "12px",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  backgroundColor:
                    activeTab === tab.key ? "#ffffff" : "transparent",
                  transition: "background-color 0.15s ease",
                }}
                onClick={() => setActiveTab(tab.key)}
              >
                <i className={tab.icon} style={{ fontSize: "13px" }} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === "ip_details" ? (
          <div className="d-flex flex-column gap-0.5">
          {/* 1. Study & Product Details */}
          <SectionCard
            index={1}
            icon="ri-microscope-line"
            title="Study & Product Details"
            subtitle="Study protocol, sponsor, and investigational product information."
          >
            <Row>
              <DetailField label="Study Code" value={data.studycode} columns={3} />
              <DetailField label="Sponsor Name" value={data.sponsorname} columns={3} />
              <DetailField label="Regulatory of Study Submission" value={data.regulatoryofstudy} columns={3} truncate={true} />
              <Col md={3} className="mb-3">
                <label className="text-muted fs-12 mb-1 text-uppercase fw-600 ls-1 d-block">
                  Type of Investigational Product (IP Category)
                </label>
                <div style={{ minHeight: "24px" }}>{categoryBadge}</div>
              </Col>
              <DetailField label="Product Name (with dosage form and strength)" value={data.productname} columns={3} truncate={true} />
              <DetailField label="Generic Name of the Product" value={data.genericname} columns={3} truncate={true} />
              <DetailField label="Strength" value={data.strength} columns={3} truncate={true} />
              <DetailField label="Dosage Form" value={data.dosageformtext} columns={3} />
              <DetailField label="Label Claim" value={data.labelclaim} columns={3} truncate={true} />
              <DetailField label="Light Condition" value={data.lightconditiontext} columns={3} />
              <DetailField label={data.batchorlot === "lot" ? "Lot No." : "Batch No."} value={data.batchlotno} columns={3} truncate={true} />
              <DetailField label="Manufacturer Label" value={data.manufacturerlabeltext} columns={3} />
              <DetailField label="Manufacturer Name" value={data.manufacturername} columns={3} truncate={true} />
              <DetailField
                label="Manufacturing Date"
                value={data.manufacturingdate ? formatDate(data.manufacturingdate) : null}
                columns={3}
              />
              <DetailField
                label={data.typeofdate === "expiry" ? "Expiry Date" : "Retest Date"}
                value={data.expirydate ? formatDate(data.expirydate) : null}
                columns={3}
              />
            </Row>
          </SectionCard>

          {/* 2. Batch & Delivery Information */}
          <SectionCard
            index={2}
            icon="ri-archive-line"
            title="Batch & Delivery Information"
            subtitle="Provide batch numbers, quantities, and delivery details."
          >
            <Row>
              <DetailField
                label="Received Quantity"
                value={(data.receivedquantity ?? "0")}
                columns={3}
              />
              <DetailField
                label="Actual received quantity"
                value={(data.actualreceivedqty ?? "0")}
                columns={3}
              />
              <DetailField label="Unit" value={data.unittext || data.unit} columns={3} />
              <DetailField
                label="Date of Receipt"
                value={data.dateofreceipt ? formatDate(data.dateofreceipt) : null}
                columns={3}
              />
              <DetailField label="Total Containers" value={data.totalcontainers} columns={3} />
              <DetailField label="Mode of Receipt" value={data.modeofreceipttext} columns={3} />
              <DetailField label="Packing Condition" value={data.packingconditiontext} columns={3} />
              {(data.packingconditionremarks || String(data.packingconditiontext || "").trim().toLowerCase() === "unsealed") && (
                <DetailField label="Packing Condition Remarks" value={data.packingconditionremarks} columns={3} truncate={true} />
              )}
              <DetailField label="Quantity required for study" value={data.qtyforstudy} columns={3} />
              {data.qtyforstudy === "Insufficient" && (
                <DetailField label="Remarks for Study Quantity" value={data.qtyforstudyremarks} columns={3} truncate={true} />
              )}
              <DetailField label="Quantity required for Retention (if applicable)" value={data.qtyforretention} columns={3} />
              {data.qtyforretention === "Insufficient" && (
                <DetailField label="Remarks for Retention Quantity" value={data.qtyforretentionremarks} columns={3} truncate={true} />
              )}
            </Row>

            {/* Container Management Table */}
            {Array.isArray(data.containers) && data.containers.length > 0 && (
              <div className="mt-4 p-3 border rounded-3 bg-light shadow-sm">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h6 className="fw-bold mb-1 text-dark d-flex align-items-center" style={{ fontSize: "13px" }}>
                      <i className="ri-list-check-2 me-2 text-primary" style={{ fontSize: "15px" }}></i>
                      Container Management
                    </h6>
                    <small className="text-muted" style={{ fontSize: "11px" }}>
                      Container details, quantities, status, and remarks.
                    </small>
                  </div>
                </div>

                <div className="table-responsive bg-white border rounded">
                  <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "12px" }}>
                    <thead className="table-light">
                      <tr>
                        <th className="text-center" style={{ width: "120px" }}>Container Number</th>
                        <th className="text-center" style={{ width: "180px" }}>Container Type</th>
                        <th className="text-center" style={{ width: "140px" }}>Container Quantity</th>
                        <th className="text-center" style={{ width: "160px" }}>Container Condition</th>
                        <th className="text-center" style={{ width: "100px" }}>Not For Use</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.containers.map((c: any, idx: number) => (
                        <tr key={idx}>
                          <td className="text-center">
                            <span className="badge bg-light text-dark border fw-bold px-2.5 py-1">
                              {c.containernumber || String(idx + 1).padStart(2, "0")}
                            </span>
                          </td>
                          <td className="text-center">{c.containertypetext || "—"}</td>
                          <td className="text-center fw-semibold">{c.initialquantity ?? "—"}</td>
                          <td className="text-center">
                            {c.containerconditiontext || c.containercondition ? (
                              (() => {
                                const condStr = String(c.containerconditiontext || c.containercondition).trim().toLowerCase();
                                const badgeClass =
                                  condStr === "sealed"
                                    ? "bg-success-subtle text-success border border-success"
                                    : condStr.includes("broken") 
                                      ? "bg-danger-subtle text-danger border border-danger"
                                      : "bg-warning-subtle text-warning border border-warning";
                                return (
                                  <span className={`badge ${badgeClass} px-2 py-1`}>
                                    {c.containerconditiontext || c.containercondition}
                                  </span>
                                );
                              })()
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              className="form-check-input my-0"
                              checked={c.isusable === false}
                              disabled
                              readOnly
                            />
                          </td>
                          <td className="text-muted fst-italic"><TruncatedText text={c.containerremarks || "—"} maxWidth="350px" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </SectionCard>

          {/* 3. Storage & Condition Assessment */}
          <SectionCard
            index={3}
            icon="ri-temp-cold-line"
            title="Storage & Condition Assessment"
            subtitle="Assess storage requirements and physical condition on receipt."
          >
            <Row>
              <DetailField
                label="Required Storage Condition"
                value={data.storageconditiontext || data.storagecondition}
                columns={3}
                truncate={true}
              />
            </Row>
          </SectionCard>

          {/* 4. Data Logger Details */}
          <SectionCard
            index={4}
            icon="ri-cpu-line"
            title="Data Logger Details"
            subtitle="Record data logger accompaniment, ID, printout, temperature excursion, and recorder details."
          >
            <Row>
              <DetailField
                label="Data Logger Accompanied"
                value={
                  String(data.isdatalogger) === "true"
                    ? "Yes"
                    : "No"
                }
                columns={3}
              />
              {(String(data.isdatalogger) === "true" ) ? (
                <>
                  <DetailField label="Data Logger ID No." value={data.dataloggerid} columns={3} truncate={true} />
                  <DetailField
                    label="Printout of Data Logger Obtained"
                    value={
                      data.loggerprintobtained === "yes"
                        ? "Yes"
                        : data.loggerprintobtained === "no"
                          ? "No"
                          : data.loggerprintobtained === "na"
                            ? "N/A"
                            : data.loggerprintobtained || "—"
                    }
                    columns={3}
                  />
                  <DetailField
                    label="Temp. Excursion Acceptable"
                    value={
                      data.tempexcursionacceptable === "yes"
                        ? "Yes"
                        : data.tempexcursionacceptable === "no"
                          ? "No"
                          : data.tempexcursionacceptable === "na"
                            ? "N/A"
                            : data.tempexcursionacceptable || "—"
                    }
                    columns={3}
                  />
                  {(data.dataloggerremarks ||
                    data.loggerprintobtained === "no" ||
                    data.loggerprintobtained === "na" ||
                    data.tempexcursionacceptable === "no" ||
                    data.tempexcursionacceptable === "na") && (
                    <DetailField label="Data Logger Remarks" value={data.dataloggerremarks} columns={3} truncate={true} />
                  )}
                </>
              ) : (
                <DetailField label="Data Logger Remarks" value={data.dataloggerremarks} columns={3} truncate={true} />
              )}
            </Row>
          </SectionCard>

          {/* 5. Pharmacy / Quarantine Details */}
          <SectionCard
            index={5}
            icon="ri-shield-check-line"
            title="Pharmacy / Quarantine Details"
            subtitle="Record allotted pharmacy ID, quarantine details, instrument IDs, and storage dates."
          >
            <Row>
              <DetailField label="Allotted Serially Pharmacy ID No." value={data.allottedpharmacyid} columns={3} truncate={true} />
              <DetailField label="Quarantine Instrument ID No." value={data.quarantineinstrumentid} columns={3} truncate={true} />
              <DetailField
                label="Date of IMP Stored in Quarantine"
                value={data.quarantinestartdate ? formatDate(data.quarantinestartdate) : null}
                columns={3}
              />
              <DetailField
                label="Date of IMP Removed from Quarantine"
                value={data.quarantineenddate ? formatDate(data.quarantineenddate) : null}
                columns={3}
              />
            </Row>
          </SectionCard>

          {/* 6. COA Details */}
          <SectionCard
            index={6}
            icon="ri-file-list-3-line"
            title="COA Details"
            subtitle="Verify COA information and label information compliance across all parameters."
          >
            <Row className="mb-3">
              <DetailField
                label="COA Receipt Date"
                value={data.coareceiptdate ? formatDate(data.coareceiptdate) : null}
                columns={3}
              />
            </Row>
            {coaList && coaList.length > 0 ? (
              <CoaDetailsTable coaList={coaList} onCoaChange={() => { }} isDisabled={true} />
            ) : (
              <div className="text-muted fst-italic py-2" style={{ fontSize: "12px" }}>
                No COA details recorded.
              </div>
            )}
          </SectionCard>

          {/* 7. Attachments & Verification */}
          <SectionCard
            index={7}
            icon="ri-attachment-2"
            title="Attachments & Verification"
            subtitle="Upload supporting documents and confirm receipt details."
          >
            {/* Attachments */}
            {data.attachments && data.attachments.length > 0 ? (
              <div className="mt-1 d-flex flex-column gap-2">
                <p className="text-muted fs-12 text-uppercase fw-medium ls-1 mb-2 d-block">Uploaded Documents</p>
                <Row className="gap-2 px-0 w-100 mx-auto">
                  {data.attachments.map((file: any, idx: number) => (
                    <Col key={idx} md={3} className={`px-0 ${addVolunteerStyle.ipInventory_wrapper}`}>
                      <div className={`${addVolunteerStyle.attachmentCard} h-100`}>
                        {/* FILE INFO */}
                        <div className={addVolunteerStyle.fileInfo}>
                          <div className={addVolunteerStyle.fileIcon}>
                            <i className="ri-file-pdf-line text-danger" />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className={addVolunteerStyle.fileName}>
                              <TruncatedText text={file.filename} />
                            </div>
                            <div className={addVolunteerStyle.fileMeta}>
                              <span className="text-uppercase">{file.attachmenttype}</span>
                            </div>
                          </div>
                        </div>

                        <div className={addVolunteerStyle.fileActions}>
                          <i
                            className="ri-eye-line text-primary"
                            title="Preview"
                            style={{ cursor: "pointer" }}
                            onClick={() => handlePreview(file)}
                          />
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            ) : (
              <div className="mt-3">
                <p className="text-muted fs-12 text-uppercase fw-medium ls-1 mb-2 d-block">Uploaded Documents</p>
                <span className="text-muted fst-italic">No attachments uploaded.</span>
              </div>
            )}

            {/* Verified By */}
            {data?.verifiedbyname && (
              <Row className="mt-2">
                <Col md={6}>
                  <label className="form-label text-muted fw-medium fs-12 mb-2 text-uppercase ls-1 d-block mt-4">
                    Verified By
                  </label>
                  <div className="d-flex align-items-center gap-2 p-1">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        background: "#e8f0fe",
                        color: "#3c6bc4",
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      {data.verifiedbyname
                        .split(" ")
                        .map((n: string) => n[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2)}
                    </div>

                    <div>
                      <div className="fw-bold text-dark" style={{ fontSize: 14, lineHeight: 1.2 }}>
                        {data.verifiedbyname}
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {data.verifiedbyrole}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {/* Comments */}
            <Row className="mt-3">
              <DetailField
                label="Additional Comments / Discrepancies"
                value={data.comments}
                columns={12}
                expandable={true}
                maxChars={150}
              />
            </Row>
          </SectionCard>
        </div>
      ) : (
          <div className="d-flex flex-column gap-0.5">
            {(() => {
              const accRaw = Array.isArray(data.acceptancedetails)
                ? data.acceptancedetails[0]
                : data.acceptancedetails;
              if (!accRaw) {
                return (
                  <SectionCard
                    index={1}
                    icon="ri-checkbox-circle-line"
                    title="IP Description & Acceptance Verification"
                    subtitle="Recorded acceptance details, container verification, and observations."
                  >
                    <div className="text-muted fst-italic py-4 text-center fs-13">
                      <i className="ri-information-line me-1 text-info fs-16 align-middle" />
                      No IP Acceptance details recorded for this inventory item.
                    </div>
                  </SectionCard>
                );
              }

              const nestedDetails = accRaw.acceptancedetails || {};
              const acc = {
                ...accRaw,
                comments: accRaw.comments ?? nestedDetails.comments ?? null,
                ipdescription: accRaw.ipdescription ?? nestedDetails.ipdescription ?? null,
                assaydifference: accRaw.assaydifference ?? nestedDetails.assaydifference ?? null,
                primarycontainer: accRaw.primarycontainer ?? nestedDetails.primarycontainer ?? null,
              };

              return (
                <SectionCard
                  index={1}
                  icon="ri-checkbox-circle-line"
                  title="IP Description & Acceptance Verification"
                  subtitle="Recorded acceptance details, container verification, and observations."
                >
                  <Row>
                    <DetailField
                      label="IP Acceptance Date"
                      value={acc.acceptancedate ? formatDate(acc.acceptancedate) : null}
                      columns={3}
                    />
                    <DetailField
                      label="Containers/pack selected for IP description"
                      value={acc.selectedcontainer}
                      columns={3}
                    />
                    <DetailField
                      label="Primary container (blister, strip and bottle) opened for IP description"
                      value={
                        acc.primarycontainer === "yes"
                          ? "Yes"
                          : acc.primarycontainer === "no"
                          ? "No"
                          : acc.primarycontainer
                      }
                      columns={3}
                    />
                    <DetailField
                      label="No. of IP unit removed for Training"
                      value={acc.trainingqty}
                      columns={3}
                    />
                    <DetailField
                      label="No. of IP unit removed for physical verification"
                      value={acc.physicalverificationqty}
                      columns={3}
                    />
                    <DetailField
                      label="Assay value difference between Test versus Reference (%)"
                      value={acc.assaydifference}
                      columns={3}
                    />
                    <DetailField
                      label="Description of IP"
                      value={acc.ipdescription}
                      columns={6}
                      truncate={true}
                    />
                    <DetailField
                      label="Comments"
                      value={acc.comments}
                      columns={6}
                      truncate={true}
                    />
                  </Row>
                </SectionCard>
              );
            })()}
          </div>
        )}
        <FilePreviewModal
          file={previewFile}
          fileUrl={previewFileUrl}
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      </Container>
    </div>
  );
};

export default ViewIpInventory;